import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import AsyncButton from '@wojtekmaj/react-async-button';
import T, { useTranslation } from '@wojtekmaj/react-t';

import { toast } from '@rewardopl/react-toast';

import Button from '@rewardopl/react-ui/button';
import CheckboxField from '@rewardopl/react-ui/checkbox_field';
import InputField from '@rewardopl/react-ui/input_field';

import { post } from '@rewardopl/utils/network';

import { Bold, ButtonWrapper, Form, Heading } from './form.styles';

import MarketingAndAgeControls from '../../../common/marketing_and_age_controls';
import MarketingDisclaimers from '../../../common/marketing_disclaimers';
import PrivacyPolicyLink from '../../../privacy_policy_link';
import TermsAndConditionsLink from '../../../terms_and_conditions_link';
import BirthdayField from './birthday_field';

import { cardSubscriptionsState, maybeCurrentUserState } from '../../../../store';

import { APP_ID, VENDOR_CARD_ID } from '../../../../constants';

import type { CardSubscription, User } from '@rewardopl/types';

const pendingConfig = {
  disabled: true,
};

export default function RegisterWithDetailsStepDetailsForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const setCurrentUser = useSetAtom(maybeCurrentUserState);
  const setCardSubscriptions = useSetAtom(cardSubscriptionsState);
  const registerErrorString = useTranslation('Failed to register');
  const userAlreadyExistsString = useTranslation('User already exists');
  const passwordInvalidString = useTranslation(
    'Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character.',
  );

  const {
    email,
    password,
    referralId,
    returnPath = '/',
  } = (location.state || {}) as {
    email: string;
    password: string;
    referralId?: string;
    returnPath?: string;
  };

  const [tandcAccepted, setTandcAccepted] = useState(false);
  const [privacyPolicyAccepted, setPrivacyPolicyAccepted] = useState(false);
  const [is16OrOlder, setIs16OrOlder] = useState<boolean | null>(null);
  const [marketingEmailAccepted, setMarketingEmailAccepted] = useState(false);
  const [marketingSmsAccepted, setMarketingSmsAccepted] = useState(false);
  const [marketingProfilingAccepted, setMarketingProfilingAccepted] = useState(false);

  const termsAccepted = tandcAccepted && privacyPolicyAccepted;
  const marketingAccepted =
    marketingEmailAccepted || marketingSmsAccepted || marketingProfilingAccepted;

  const action = '/api/users';

  async function onClick(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    event.preventDefault();

    const submitButton = event.currentTarget;
    const form = submitButton.form;

    if (!form || !form.reportValidity()) {
      return;
    }

    const formData = new FormData(form);
    const filteredFormData = new FormData();

    for (const [key, value] of formData.entries()) {
      if (value !== '') {
        filteredFormData.append(key, value);
      }
    }

    filteredFormData.append('email', email);
    filteredFormData.append('password', password);
    filteredFormData.append('terms_accepted', termsAccepted.toString());
    if (is16OrOlder !== null) {
      filteredFormData.append('marketing_accepted', marketingAccepted.toString());
      filteredFormData.append('marketing_email_accepted', marketingEmailAccepted.toString());
      filteredFormData.append('marketing_sms_accepted', marketingSmsAccepted.toString());
      filteredFormData.append(
        'marketing_profiling_accepted',
        marketingProfilingAccepted.toString(),
      );
    }
    if (referralId) {
      filteredFormData.append('referral_id', referralId);
    }

    try {
      const user = (await post(action, {
        headers: {
          'app-id': APP_ID,
        },
        body: filteredFormData,
      })) as User;

      const response = (await post('/api/users/current/card_subscriptions', {
        body: { card_id: VENDOR_CARD_ID },
      })) as CardSubscription;

      setCurrentUser(user);

      setCardSubscriptions([response]);
    } catch (err) {
      const error = err;

      const errorString = (() => {
        if (!(error instanceof Error)) {
          return registerErrorString;
        }

        switch (error.message) {
          case 'Password does not meet complexity requirements':
            return passwordInvalidString;
          case 'User already exists':
            return userAlreadyExistsString;
          default:
            return registerErrorString;
        }
      })();

      toast.error(errorString);

      if (error instanceof Error && error.message === 'User already exists') {
        navigate('/login/email', { state: { email: formData.get('email'), returnPath } });
      }
    }
  }

  return (
    <Form action={action} method="POST">
      <Heading>
        <T>Tell us a bit about yourself</T>
      </Heading>
      <InputField
        autoComplete="given-name"
        label="First name"
        name="first_name"
        required={false}
        type="text"
      />
      <Bold>
        <T>Share your birthday and receive a gift on your big day</T>
      </Bold>
      <BirthdayField label="Birthday" name="birthday" required={false} />
      <InputField
        autoComplete="tel"
        label="Mobile"
        name="mobile"
        required={marketingSmsAccepted}
        type="tel"
      />
      <MarketingAndAgeControls
        form="_private"
        is16OrOlder={is16OrOlder}
        marketingEmailAccepted={marketingEmailAccepted}
        marketingSmsAccepted={marketingSmsAccepted}
        marketingProfilingAccepted={marketingProfilingAccepted}
        setIs16OrOlder={setIs16OrOlder}
        setMarketingEmailAccepted={setMarketingEmailAccepted}
        setMarketingSmsAccepted={setMarketingSmsAccepted}
        setMarketingProfilingAccepted={setMarketingProfilingAccepted}
      />
      <MarketingDisclaimers />
      <CheckboxField
        checked={tandcAccepted}
        name="tandc_accepted"
        label={
          <>
            <T>I have read and agree to the</T> <TermsAndConditionsLink />.
          </>
        }
        onChange={() => setTandcAccepted(!tandcAccepted)}
        required
      />
      <CheckboxField
        checked={privacyPolicyAccepted}
        name="privacy_policy_accepted"
        label={
          <>
            <T>I have read and agree to the</T> <PrivacyPolicyLink />.
          </>
        }
        onChange={() => setPrivacyPolicyAccepted(!privacyPolicyAccepted)}
        required
      />
      <ButtonWrapper>
        <AsyncButton
          as={Button}
          className="primary"
          onClick={onClick}
          pendingConfig={pendingConfig}
          type="submit"
        >
          <T>Register</T>
        </AsyncButton>
      </ButtonWrapper>
    </Form>
  );
}
